// src/contexts/MarketingStudioContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useOrganization } from './OrganizationContext';
import { useUser } from './UserContext';
import { getFirestore, collection, query, where, getDocs, doc, setDoc, deleteDoc, DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { getApp } from 'firebase/app';

interface MarketingStudioContextType {
  // AI Provider configuration
  aiProvider: 'leonardo' | 'stability' | 'mock';
  setAIProvider: (provider: 'leonardo' | 'stability' | 'mock') => void;
  
  // Organization branding info
  organizationBranding: {
    logo?: string;
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    brandVoice: string;
    targetAudience: string;
    industry: string;
  };
  
  // Campaign tracking
  campaigns: Campaign[];
  addCampaign: (campaign: Campaign) => void;
  updateCampaign: (id: string, campaign: Partial<Campaign>) => void;
  deleteCampaign: (id: string) => void;
  
  // Asset management
  marketingAssets: MarketingAsset[];
  addMarketingAsset: (asset: MarketingAsset) => Promise<string | null>;
  deleteMarketingAsset: (id: string) => void;
  
  // Studio state
  loading: boolean;
  error: string | null;
}

export interface Campaign {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  startDate: string;
  endDate: string;
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'archived';
  platforms: string[];
  assets: string[]; // IDs of assets associated with this campaign
  content: {
    title: string;
    description: string;
    callToAction: string;
  };
  performance?: {
    impressions: number;
    clicks: number;
    conversions: number;
  };
  schedule: {
    frequency: 'once' | 'daily' | 'weekly' | 'monthly';
    times: string[]; // ISO date strings
  };
  createdAt: string;
  updatedAt: string;
}

export interface MarketingAsset {
  id: string;
  name: string;
  type: 'image' | 'video' | 'document' | 'content';
  url: string;
  thumbnailUrl?: string;
  organizationId: string;
  campaignId?: string;
  metadata: {
    prompt?: string;
    aiProvider: 'leonardo' | 'stability' | 'mock';
    dimensions?: {
      width: number;
      height: number;
    };
    platform?: 'facebook' | 'instagram' | 'twitter' | 'linkedin' | 'email' | 'web';
    coverImage?: string; // Added for newsletter cover images
    content?: string; // Store generated content text
  };
  tags: string[];
  createdAt: string;
  generatedBy: string; // user ID
}

const defaultBranding = {
  primaryColor: '#2196F3',
  secondaryColor: '#FFA000',
  fontFamily: 'Roboto, sans-serif',
  brandVoice: 'Professional and friendly',
  targetAudience: 'General audience',
  industry: 'Travel and Tourism',
};

const MarketingStudioContext = createContext<MarketingStudioContextType | undefined>(undefined);

export const MarketingStudioProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentOrganization } = useOrganization();
  const { currentUser } = useUser();
  
  const [aiProvider, setAIProvider] = useState<'leonardo' | 'stability' | 'mock'>(
    process.env.REACT_APP_STABILITY_AI_API_KEY ? 'stability' : 
    process.env.REACT_APP_LEONARDO_AI_API_KEY ? 'leonardo' : 'mock'
  );
  
  const [organizationBranding, setOrganizationBranding] = useState({
    ...defaultBranding,
    logo: currentOrganization?.logoUrl,
  });
  
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [marketingAssets, setMarketingAssets] = useState<MarketingAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Update branding when organization changes
  useEffect(() => {
    if (currentOrganization) {
      setOrganizationBranding(prev => ({
        ...prev,
        logo: currentOrganization.logoUrl,
        primaryColor: currentOrganization.brandColor || prev.primaryColor,
        secondaryColor: currentOrganization.secondaryColor || prev.secondaryColor,
        // Other brand attributes would be loaded from organization profile
      }));
      
      // Load campaigns and assets for this organization
      loadCampaignsForOrganization(currentOrganization.id);
      loadAssetsForOrganization(currentOrganization.id);
    }
  }, [currentOrganization]);
  
  // Mock function for loading campaigns - would be replaced with Firestore
  const loadCampaignsForOrganization = async (orgId: string) => {
    setLoading(true);
    try {
      // In a real implementation, this would fetch from Firestore
      setCampaigns([]);
      setLoading(false);
    } catch (err) {
      setError('Failed to load campaigns');
      setLoading(false);
    }
  };
  
  // Load marketing assets from Firestore
  const loadAssetsForOrganization = async (orgId: string) => {
    setLoading(true);
    try {
      const db = getFirestore(getApp());
      const assetsCollection = collection(db, 'marketingAssets');
      const q = query(assetsCollection, where('organizationId', '==', orgId));
      
      const snapshot = await getDocs(q);
      const assets: MarketingAsset[] = [];
      
      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data() as DocumentData;
        assets.push({
          id: docSnapshot.id,
          ...data
        } as MarketingAsset);
      });
      
      setMarketingAssets(assets);
      setLoading(false);
    } catch (err) {
      console.error('Error loading assets:', err);
      setError('Failed to load assets');
      setLoading(false);
    }
  };
  
  // Add marketing asset with Firestore persistence
  const addMarketingAsset = async (asset: MarketingAsset) => {
    try {
      setLoading(true);
      
      // Store in Firestore
      const db = getFirestore(getApp());
      const assetRef = doc(db, 'marketingAssets', asset.id);
      
      await setDoc(assetRef, {
        ...asset,
        updatedAt: new Date().toISOString(),
      });
      
      // Update local state
      setMarketingAssets(prev => [...prev, asset]);
      setLoading(false);
      
      return asset.id;
    } catch (err) {
      console.error('Error adding asset:', err);
      setError('Failed to add asset');
      setLoading(false);
      return null;
    }
  };
  
  // Delete marketing asset with Firestore persistence
  const deleteMarketingAsset = async (id: string) => {
    try {
      setLoading(true);
      
      // Delete from Firestore
      const db = getFirestore(getApp());
      await deleteDoc(doc(db, 'marketingAssets', id));
      
      // Update local state
      setMarketingAssets(prev => prev.filter(asset => asset.id !== id));
      setLoading(false);
    } catch (err) {
      console.error('Error deleting asset:', err);
      setError('Failed to delete asset');
      setLoading(false);
    }
  };
  
  // Campaign management functions
  const addCampaign = (campaign: Campaign) => {
    setCampaigns(prev => [...prev, campaign]);
    // In a real implementation, save to Firestore
  };
  
  const updateCampaign = (id: string, campaignUpdates: Partial<Campaign>) => {
    setCampaigns(prev => 
      prev.map(campaign => 
        campaign.id === id ? { ...campaign, ...campaignUpdates, updatedAt: new Date().toISOString() } : campaign
      )
    );
    // In a real implementation, update in Firestore
  };
  
  const deleteCampaign = (id: string) => {
    setCampaigns(prev => prev.filter(campaign => campaign.id !== id));
    // In a real implementation, delete from Firestore
  };
  
  const value: MarketingStudioContextType = {
    aiProvider,
    setAIProvider,
    organizationBranding,
    campaigns,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    marketingAssets,
    addMarketingAsset,
    deleteMarketingAsset,
    loading,
    error,
  };
  
  return (
    <MarketingStudioContext.Provider value={value}>
      {children}
    </MarketingStudioContext.Provider>
  );
};

export const useMarketingStudio = (): MarketingStudioContextType => {
  const context = useContext(MarketingStudioContext);
  if (context === undefined) {
    throw new Error('useMarketingStudio must be used within a MarketingStudioProvider');
  }
  return context;
};
